import { Component, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import { AutoCompleteDropdownComponent } from '../../../../shared/controls/auto-complete-dropdown/auto-complete-dropdown.component';
import { SwitchControlComponent } from '../../../../shared/controls/switch-control/switch-control.component';

import { ToastService } from '../../../../shared/toast/toast.service';
import { ErrorService } from '../../../../shared/services/error-service';

import {
    ClientService,
    CreateClientAreaInput,
    AllInsurersResult,
    AllIndustryItem,
    IndustryService,
    InsurerService,
    ClientAreaService,
    ClientAreaByIdResult,
    UpdateClientAreaInput,
    CreateResult,
    PolicyGroupService
} from '../../../../../service/proxy';

@Component({
    selector: 'client-area-detail',
    templateUrl: './client-area-detail.component.html',
    styleUrl: './client-area-detail.component.scss',
    standalone: true,

    imports: [
        CommonModule,
        FormsModule,
        AutoCompleteDropdownComponent,
        SwitchControlComponent
    ],

    providers: [
        ClientService,
        ClientAreaService,
        InsurerService,
        IndustryService,
        PolicyGroupService
    ]
})
export class ClientAreaDetailComponent implements OnInit {
    @ViewChild('form')
    form: NgForm | undefined;

    public clientId: number = 0;
    public policyId: number = 0;

    public policy: any = {};
    public client: any = {};
    public insurers: { id: number; name: string }[] = [];
    public industries: AllIndustryItem[] = [];
    public policyGroups: { id: number; name: string; isSelected: boolean }[] = [];

    public selectedIndustryId: number | undefined = undefined;
    public selectedPolicyBaseId: number | undefined = undefined;
    public selectedPolicyGroupId: number | undefined = undefined;

    public policyBaseOptions: { id: number; description: string }[] = [
        { id: 1, description: 'Insured turnover' },
        { id: 2, description: 'Whole turnover' },
        { id: 3, description: 'Insured outstanding balances' },
        { id: 4, description: 'Fixed premium' },
        { id: 5, description: 'Credit limit' }
    ];

    public showGroupPanel: boolean = false;
    public groupPanelCaption: string = 'Assign Policy Groups';
    public groupSearch: string = '';

    public isSaving: boolean = false;
    public formSubmitted: boolean = false;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private toastService: ToastService,
        private errorService: ErrorService,
        private clientAreaService: ClientAreaService,
        private policyGroupService: PolicyGroupService,
        private insurerService: InsurerService,
        private industryService: IndustryService) {}

    ngOnInit(): void {
        this.route.paramMap.subscribe(params => {
            this.clientId = Number(params.get('clientId'));
            this.policyId = Number(params.get('policyId'));
            this.policy.clientId = this.clientId;

            if (this.policyId > 0) {
                this.getPolicyById();
            }
            else {
                this.getInsurersForLookup();
                this.getIndustriesForLookup();
                this.getPolicyGroupsForPolicy();
            }
        });
    }

    getPolicyById(): void {
        this.clientAreaService.getClientAreaById(this.policyId)
            .subscribe({
                next: (result: ClientAreaByIdResult) => {
                    this.policy = result;

                    if (result.policyBase) {
                        const match = this.policyBaseOptions.find(opt => opt.description === result.policyBase);
                        this.selectedPolicyBaseId = match
                            ? match.id
                            : undefined;
                    }
                    else {
                        this.selectedPolicyBaseId = undefined;
                    }

                    this.policy.inceptionDate = this.formatDateForInput(result.inceptionDate);
                    this.selectedIndustryId = result.industryId == null ? undefined : Number(result.industryId);
                    this.selectedPolicyGroupId = result.policyGroup == null ? undefined : Number(result.policyGroup);
                },
                error: (error: any) => {
                    this.errorService.handleError(error);
                },
                complete: () => {
                    this.getInsurersForLookup();
                    this.getIndustriesForLookup();
                    this.getPolicyGroupsForPolicy();
                }
            }
        );
    }

    formatDateForInput(date: string | Date): string {
        const d = new Date(date);
        return d.toISOString().split('T')[0];
    }

    getInsurersForLookup(): void {
        this.insurerService.getAllInsurers()
            .subscribe({
                next: (result: AllInsurersResult) => {
                    this.insurers = result.results.map(insurer => ({
                        id: insurer.id,
                        name: insurer.insurerName
                    }));
                },
                error: (error: any) => {
                    this.errorService.handleError(error);
                }
            }
        );
    }

    getIndustriesForLookup(): void {
        this.industryService.getAllIndustries()
            .subscribe({
                next: (data) => {
                    this.industries = data.results || [];
                },
                error: (err) => {
                    this.errorService.handleError(err);
                }
            }
        );
    }

    onInsurerSelectionChange(selectedId: number | null): void {
        this.policy.insurerId = selectedId ?? undefined;
    }

    onIndustrySelectionChange(selectedId: number | null): void {
        this.selectedIndustryId = selectedId ?? undefined;
        this.policy.industryId = selectedId ?? undefined;
    }

    onPolicyBaseSelectionChange(selectedId: number | null): void {
        this.selectedPolicyBaseId = selectedId ?? undefined;
        this.policy.policyBase = this.policyBaseOptions.find(option => option.id === selectedId)?.description || '';
    }

    onPolicyGroupSelectionChange(selectedId: number | null): void {
        this.selectedPolicyGroupId = selectedId ?? undefined;
        this.policy.policyGroupId = selectedId ?? undefined;
    }

    getPolicyGroupsForPolicy(): void {
        if (!this.clientId) {
            return;
        }

        this.policyGroupService.getAllPolicyGroups()
            .subscribe({
                next: (result: any) => {
                    this.policyGroups = result.results;
                    this.selectedPolicyGroupId = Number(this.policy.policyGroup);
                },
                error: (error: any) => {
                    this.errorService.handleError(error);
                }
            });
    }

    createOrUpdatePolicy(form: any): void {
        this.formSubmitted = true;

        if (!form.valid || !this.policy.insurerId || !this.selectedPolicyBaseId) {
            return;
        }

        this.isSaving = true;

        if (this.policyId === 0) {
            this.createPolicy();
        }
        else {
            this.updatePolicy();
        }
    }

    createPolicy() {
        const input = this.policy as CreateClientAreaInput;

        if (this.selectedIndustryId) {
            input.industryId = Number(this.selectedIndustryId);
        }

        input.policyBase = this.policy.policyBase;

        this.clientAreaService.createClientArea(input)
            .subscribe({
                next: (result: CreateResult) => {
                    this.router.navigate(['/client-area', this.clientId, result.id]);
                    this.toastService.show('Policy created successfully.', 'success');
                    this.isSaving = false;
                },
                error: (error: any) => {
                    this.errorService.handleError(error);
                }
            }
        );
    }

    updatePolicy() {
        const input = this.policy as UpdateClientAreaInput;

        if (this.selectedIndustryId) {
            input.industryId = Number(this.selectedIndustryId);
        }

        input.inceptionDate = new Date(input.inceptionDate);
        input.policyBase = this.policy.policyBase;
        input.policyGroupId = this.selectedPolicyGroupId ?? 0;

        this.clientAreaService.updateClientArea(this.policyId, input)
            .subscribe({
                next: () => {
                    this.toastService.show('Policy updated successfully.', 'success');
                    this.isSaving = false;
                },
                error: (error: any) => {
                    this.errorService.handleError(error);
                }
            }
        );
    }

    onClear(): void {
        if (this.policyId > 0) {
            this.getPolicyById();
        }
        else {
            this.getInsurersForLookup();
            this.getIndustriesForLookup();
            this.getPolicyGroupsForPolicy();
            this.policy.insurerId = undefined;
            this.selectedIndustryId = undefined;
            this.selectedPolicyGroupId = undefined;
            this.selectedPolicyBaseId = undefined;

            if (this.form) {
                this.form.resetForm();
            }
        }
    }

    addAnotherPolicy(): void {
        this.router.navigate([`/client-area/${this.clientId}/0`]);

        this.getInsurersForLookup();
        this.getIndustriesForLookup();
        this.getPolicyGroupsForPolicy();
        this.policy.insurerId = undefined;
        this.selectedIndustryId = undefined;
        this.selectedPolicyGroupId = undefined;
        this.selectedPolicyBaseId = undefined;

        if (this.form) {
            this.form.resetForm();
        }
    }

    displayInsurer(option: any): string {
        return option?.name || '';
    }

    displayIndustry(option: any): string {
        return option?.description || '';
    }

    displayPolicyBase(option: any): string {
        return option?.description || '';
    }

    displayPolicyGroup(option: any): string {
        return option?.name || '';
    }
}